import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import {
    Box, Checkbox, DialogContent, DialogTitle,
    FormControlLabel, Icon, IconButton, InputBase,
    MenuItem, Paper, TextField, Typography,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { DisabledFormWrapper } from 'app/main/util/clusterUtils';
import { RESOURCE_MANAGER } from '../../util/enums';
import { ipValid } from '../../util/InputValidate';
import { getRegionsGroupedByContinent, getRegionLabel, findRegionByCode, regionToLocation } from './maasRegions';

import * as formHelpers from './formHelpers';
import * as StackActions from 'app/main/stacks/store/actions/stacks.actions.js';
import * as Actions from '../store/actions';
import useClusterForm from '../hooks/useClusterForm';
import ClusterFormHeader from '../components/ClusterFormHeader';
import QuotaWarningBanner from '../components/QuotaWarningBanner';
import AdvancedSectionsBlock from '../components/AdvancedSectionsBlock';
import DescriptionSection from '../components/DescriptionSection.temporary';
import SectionCard from '../components/SectionCard';
import { inputGroupStyle } from '../components/formLayoutStyles';

const MaasCluster = (props) => {
    // These must be declared before useClusterForm because customValidation references them
    const [selectedPreconfiguredDevice, setSelectedPreconfiguredDevice] = useState(null);
    const isCommissioned = Boolean(selectedPreconfiguredDevice);

    const {
        form, setForm, setField, advanced, setAdvanced,
        classes, loading, marketplace,
        disableInputs, isFormValid, quotas,
        handleClose, handleSubmit: baseHandleSubmit, handleEdit: baseHandleEdit,
    } = useClusterForm({
        provider: 'maas', props, formHelpers,
        customValidation: (f) => {
            if (!f.displayName) return false;
            if (isCommissioned) return Boolean(selectedPreconfiguredDevice);
            return Boolean(f.credentialId && f.serverInstanceId && f.serverImageName);
        },
        onInit: (dispatch) => {
            dispatch(StackActions.getAcmImages());
            dispatch(StackActions.getAcmShapes());
            dispatch(Actions.provider_os_types('maas'));
            dispatch(Actions.getPreconfiguredDevices());
        },
    });

    const pkgRef = useRef(null);

    // MaaS-specific Redux state
    const supportedOSs = useSelector(({ clusters }) => clusters.cluster.os_types) || [];
    const acmLayers = useSelector(({ stacks }) => stacks.stack.acm_images) || [];
    const acmShapes = useSelector(({ stacks }) => stacks.stack.acm_shapes) || [];
    const preconfiguredDevices = useSelector(({ clusters }) => clusters.cluster.preconfiguredDevices) || [];
    const credentials = useSelector(({ credentials }) => credentials.credential.list) || [];

    // MaaS-specific UI state
    const [selectedDeviceFamily, setSelectedDeviceFamily] = useState(null);
    const [selectedHeadNodeShape, setSelectedHeadNodeShape] = useState(null);
    const [selectedCommissionedComputeNodes, setSelectedCommissionedComputeNodes] = useState([]);
    const [selectedRegion, setSelectedRegion] = useState(null);

    // Derived
    const headNodeImages = useMemo(() =>
        acmLayers.filter(l => l.status === 'available' && l.type === 'oddc_base' && l.image_type === 'server').map(l => ({ name: l.name })),
        [acmLayers]);
    const computeNodeImages = useMemo(() =>
        acmLayers.filter(l => l.status === 'available' && l.type === 'oddc_base' && l.image_type === 'node').map(l => ({ name: l.name })),
        [acmLayers]);
    const nodeShapes = useMemo(() =>
        (acmShapes || []).map(s => ({ name: s.description, instance_id: s.name, machines: s.machine_names, all_machines: s.all_machine_names || [] })),
        [acmShapes]);
    const filteredCredentials = useMemo(() => credentials.filter(c => c.provider === 'maas'), [credentials]);
    const regionOptions = useMemo(() => getRegionsGroupedByContinent(), []);

    // Sync commissioned data to form so toApiShape picks it up on save.
    // Mongoose requires server.instance_id/image_name, ssh_username and node.group[].instance_id —
    // populating them from the selected device avoids a validation error on save for commissioned clusters.
    useEffect(() => {
        if (!form) return;
        if (isCommissioned && selectedPreconfiguredDevice && selectedDeviceFamily) {
            // osType resolution priority:
            //   1. Per-machine detected osType (commissioned-time value from
            //      commissioningService normalizeOsType, e.g. ubuntu-26.04 on
            //      the GB10 Sparks). Authoritative — what's actually on the box.
            //   2. Family-level hint (os_type on the device family). Used as
            //      seed before any machine is selected.
            //   3. Hard fallback to ubuntu-22.04 only if nothing is known.
            // Without (1), every commissioned cluster picked the family fallback
            // (hardcoded ubuntu-22.04 in the backend) and Heidi Desktop never
            // surfaced on Ubuntu 26 commissioned machines because the UI gates
            // on cluster.info['os-type'] startsWith 'ubuntu-26'.
            const resolvedOsType = selectedPreconfiguredDevice.osType
                || selectedDeviceFamily.os_type
                || 'ubuntu-22.04';
            setField({
                deviceType: 'commissioned',
                commissionedMachineId: selectedPreconfiguredDevice.id || null,
                commissionedComputeNodes: selectedCommissionedComputeNodes,
                location: regionToLocation(selectedRegion),
                serverInstanceId: selectedPreconfiguredDevice.instance_id || '',
                serverImageName: selectedDeviceFamily.machine_type,
                serverMachine: selectedDeviceFamily.machine_type,
                osType: resolvedOsType,
                nodeGroups: [],
            });
        } else {
            setField({
                deviceType: 'standard',
                commissionedMachineId: null,
                commissionedComputeNodes: [],
                location: regionToLocation(selectedRegion),
            });
        }
    }, [isCommissioned, selectedPreconfiguredDevice, selectedDeviceFamily, selectedCommissionedComputeNodes, selectedRegion]);

    // Restore device family + commissioned machine on edit
    useEffect(() => {
        if (!form?.serverMachine || !preconfiguredDevices.length) return;
        const family = preconfiguredDevices.find(df => df.machine_type === form.serverMachine);
        if (family) {
            setSelectedDeviceFamily(family);
            if (form.commissionedMachineId) {
                const machineId = String(form.commissionedMachineId?.$oid || form.commissionedMachineId);
                for (const f of preconfiguredDevices) {
                    const match = f.devices?.find(d => String(d.id || d._id) === machineId);
                    if (match) { setSelectedPreconfiguredDevice(match); break; }
                }
            }
            if (form.commissionedComputeNodes?.length > 0) {
                setSelectedCommissionedComputeNodes(form.commissionedComputeNodes.map(id => String(id?.$oid || id)));
            }
        }
    }, [form?.serverMachine, form?.commissionedMachineId, preconfiguredDevices]);

    // Restore region on edit
    useEffect(() => {
        if (props.type === 'edit' && form?.location?.region && !selectedRegion) {
            setSelectedRegion(findRegionByCode(form.location.region) || null);
        }
    }, [props.type, form?.location, selectedRegion]);

    // Pre-select head node shape on edit
    useEffect(() => {
        if (form?.serverInstanceId && nodeShapes.length > 0 && !selectedHeadNodeShape) {
            const shape = nodeShapes.find(s => s.instance_id === form.serverInstanceId);
            if (shape) setSelectedHeadNodeShape(shape);
        }
    }, [form?.serverInstanceId, nodeShapes, selectedHeadNodeShape]);

    const handleHeadNodeShapeChange = useCallback((event, newValue) => {
        setSelectedHeadNodeShape(newValue);
        setField('serverInstanceId', newValue?.instance_id || '');
    }, [setField]);

    const getAvailableMachines = useCallback(() =>
        nodeShapes.filter(shape => shape.machines?.length > 0), [nodeShapes]);

    const addPackage = useCallback(() => {
        if (!pkgRef.current?.value) return;
        setField('packages', [...(form?.packages || []), pkgRef.current.value]);
        pkgRef.current.value = '';
    }, [form?.packages, setField]);

    const removePackage = useCallback((pkg) => {
        setField('packages', (form?.packages || []).filter(p => p !== pkg));
    }, [form?.packages, setField]);

    const handleDeviceFamilyChange = useCallback((newDeviceType) => {
        setSelectedCommissionedComputeNodes([]);
        if (newDeviceType !== 'standard') {
            const family = preconfiguredDevices.find(df => df.machine_type === newDeviceType);
            setSelectedDeviceFamily(family);
            setField('serverMachine', newDeviceType);
            if (family?.devices?.length > 0) setSelectedPreconfiguredDevice(family.devices[0]);
            else setSelectedPreconfiguredDevice(null);
        } else {
            setSelectedDeviceFamily(null);
            setSelectedPreconfiguredDevice(null);
            setField('serverMachine', '');
        }
    }, [preconfiguredDevices, setField]);

    // MaaS header — no marketplace, use form name directly
    const maasMarketplace = {
        ...marketplace,
        title: form?.hostname || form?.displayName || 'New Cluster',
        imageSrc: 'assets/images/providers/acm.png',
    };

    return (
        <React.Fragment>
            {form && (
                <React.Fragment>
                    <DialogTitle className={clsx(classes.modalHeader, 'h-54 min-h-54 p-20')}>
                        <ClusterFormHeader
                            provider="maas" providerLabel="ACM" marketplace={maasMarketplace}
                            advanced={advanced} onToggleAdvanced={() => setAdvanced(!advanced)}
                            isFormValid={isFormValid} loading={loading} disableInputs={disableInputs}
                            formType={props.type} onSave={baseHandleSubmit} onEdit={baseHandleEdit} onClose={handleClose}
                        />
                    </DialogTitle>

                    <QuotaWarningBanner quotas={quotas} />

                    <DialogContent className={clsx(classes.content, 'p-16')}>
                        <DisabledFormWrapper isDisabled={disableInputs}>
                            <div className="!w-full">

                                {/* General Section */}
                                <SectionCard icon="info" title="General">
                                    <div style={inputGroupStyle}>
                                        <TextField className={clsx(classes.input)} style={{ flex: 1 }}
                                            label="Cluster Name" variant="outlined" required
                                            value={form.displayName || ''} onChange={(e) => setField('displayName', e.target.value)} />
                                    </div>
                                    <div style={inputGroupStyle}>
                                        <TextField className={clsx(classes.input)} style={{ flex: 1 }}
                                            label="Hostname" variant="outlined"
                                            value={form.hostname || ''} onChange={(e) => setField('hostname', e.target.value)}
                                            disabled={props.type !== 'edit'}
                                            placeholder={props.type !== 'edit' ? 'Auto-generated on save' : ''} />
                                    </div>
                                    <div style={inputGroupStyle}>
                                        <TextField select className={clsx(classes.input)} style={{ flex: 1 }}
                                            label="Device Family *" variant="outlined" required
                                            value={isCommissioned ? selectedDeviceFamily?.machine_type || '' : 'standard'}
                                            onChange={(e) => handleDeviceFamilyChange(e.target.value)}
                                            SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 300 } } } }}>
                                            <MenuItem value="standard">Standard (MaaS Deploy)</MenuItem>
                                            {preconfiguredDevices.map(f => (
                                                <MenuItem key={f.machine_type} value={f.machine_type}>{f.name} (Commissioned)</MenuItem>
                                            ))}
                                        </TextField>
                                        {!isCommissioned && (
                                            <TextField select className={clsx(classes.input)} style={{ flex: 1 }}
                                                label="OS Type" variant="outlined" required
                                                value={form.osType || ''} onChange={(e) => setField('osType', e.target.value)}
                                                SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 300 } } } }}>
                                                {supportedOSs.map((os, i) => <MenuItem key={i} value={os}>{os}</MenuItem>)}
                                            </TextField>
                                        )}
                                        {!isCommissioned && (
                                            <TextField select className={clsx(classes.input)} style={{ flex: 1 }}
                                                label="Credential *" variant="outlined" required
                                                value={form.credentialId || ''} onChange={(e) => setField('credentialId', e.target.value)}
                                                SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 300 } } } }}>
                                                {filteredCredentials.map((c, i) => <MenuItem key={i} value={c.id}>{c.name}</MenuItem>)}
                                            </TextField>
                                        )}
                                        {isCommissioned && selectedDeviceFamily?.devices?.length > 0 && (
                                            <TextField select className={clsx(classes.input)} style={{ flex: 1 }}
                                                label="Head Node Device *" variant="outlined"
                                                value={selectedPreconfiguredDevice?.id || ''}
                                                onChange={(e) => {
                                                    const device = selectedDeviceFamily.devices.find(d => (d.id || d._id) === e.target.value);
                                                    setSelectedPreconfiguredDevice(device || null);
                                                }}
                                                helperText={selectedPreconfiguredDevice && (selectedPreconfiguredDevice.osType
                                                    ? 'Detected OS: ' + selectedPreconfiguredDevice.osType
                                                    : 'OS not yet detected on this machine; cluster will fall back to ' + (selectedDeviceFamily.os_type || 'ubuntu-22.04'))}>
                                                {selectedDeviceFamily.devices.map(d => (
                                                    <MenuItem key={d.id || d._id} value={d.id || d._id}>{d.name} ({d.ipAddress || d.public_ip})</MenuItem>
                                                ))}
                                            </TextField>
                                        )}
                                    </div>
                                    <div style={inputGroupStyle}>
                                        <TextField select className={clsx(classes.input)} style={{ flex: 1 }}
                                            label="Manager" variant="outlined" required
                                            value={form.manager || 'none'} onChange={(e) => setField('manager', e.target.value)}>
                                            <MenuItem value="none">None</MenuItem>
                                            <MenuItem value="slurm">Slurm</MenuItem>
                                            {!isCommissioned && <MenuItem value="torque">Torque</MenuItem>}
                                        </TextField>
                                    </div>
                                    {form.manager === RESOURCE_MANAGER.TORQUE && (
                                        <div style={inputGroupStyle}>
                                            <TextField className={clsx(classes.input)} style={{ flex: 1 }}
                                                label="Moab Hostname" variant="outlined" required
                                                value={form.moabHostname || ''} onChange={(e) => setField('moabHostname', e.target.value)} />
                                            <TextField className={clsx(classes.input)} style={{ flex: 1 }}
                                                label="Moab IP" variant="outlined" required
                                                value={form.moabIp || ''} onChange={(e) => setField('moabIp', e.target.value)}
                                                error={Boolean(form.moabIp && !ipValid(form.moabIp))}
                                                helperText={form.moabIp && !ipValid(form.moabIp) ? 'Not a valid IP' : ''} />
                                        </div>
                                    )}
                                    <Autocomplete
                                        options={regionOptions} groupBy={(o) => o.continent}
                                        getOptionLabel={(o) => getRegionLabel(o)}
                                        value={selectedRegion}
                                        onChange={(e, v) => setSelectedRegion(v)}
                                        renderInput={(params) => <TextField {...params} label="Geographic Location" variant="outlined" placeholder="Select region" />}
                                        style={{ marginBottom: '16px' }}
                                    />
                                </SectionCard>

                                {/* Head Node — standard only */}
                                {!isCommissioned && (
                                    <SectionCard icon="dns" title="Head Node">
                                        <div style={inputGroupStyle}>
                                            <Autocomplete className={clsx(classes.input)} style={{ flex: 1 }}
                                                options={headNodeImages} getOptionLabel={(o) => o.name}
                                                value={form.serverImageName ? { name: form.serverImageName } : null}
                                                onChange={(e, v) => setField('serverImageName', v?.name || '')}
                                                renderInput={(params) => <TextField {...params} label="Head Node Image" variant="outlined" required />}
                                            />
                                            <Autocomplete style={{ width: '33%' }}
                                                options={getAvailableMachines()} getOptionLabel={(o) => o.name}
                                                value={nodeShapes.find(s => s.instance_id === form.serverInstanceId) || null}
                                                onChange={handleHeadNodeShapeChange}
                                                renderInput={(params) => <TextField {...params} label="Head Node Size *" variant="outlined" />}
                                            />
                                        </div>
                                        {selectedHeadNodeShape?.all_machines?.length > 0 && (
                                            <div>
                                                <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: '8px' }}>Select Machine</Typography>
                                                <div className="flex space-x-6">
                                                    {selectedHeadNodeShape.all_machines.map(machine => (
                                                        <FormControlLabel key={machine}
                                                            control={<input type="radio" name="machine" checked={form.serverMachine === machine}
                                                                onChange={() => {
                                                                    setField('serverMachine', machine);
                                                                    const updatedGroups = (form.nodeGroups || []).map(g => ({
                                                                        ...g, machines: (g.machines || []).filter(m => m !== machine),
                                                                        count: (g.machines || []).filter(m => m !== machine).length,
                                                                    }));
                                                                    setField('nodeGroups', updatedGroups);
                                                                }} className="mr-2" />}
                                                            label={machine}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </SectionCard>
                                )}

                                {/* Compute Nodes — standard */}
                                {!isCommissioned && (
                                    <SectionCard icon="memory" title="Compute Nodes"
                                        action={<button onClick={() => {
                                            const groups = form.nodeGroups || [];
                                            if (groups.length < 250) setField('nodeGroups', [...groups, { ...groups[0], count: 1, group_node_prefix_label: `Node Group ${groups.length + 1}` }]);
                                        }} className={classes.grayButton} style={{ padding: '4px 12px', cursor: 'pointer', border: '1px solid #ccc', borderRadius: '4px', background: '#fafafa' }}>+ Add Node Group</button>}>
                                        {(form.nodeGroups || []).map((group, i) => (
                                            <Box key={`node-${i}`} mb={2} p={2} style={{ border: '1px solid #e0e0e0', borderRadius: '4px' }}>
                                                <div style={inputGroupStyle}>
                                                    <TextField className={clsx(classes.input)} style={{ flex: 1 }}
                                                        label="Label" variant="outlined" required
                                                        value={group.group_node_prefix_label || ''}
                                                        onChange={(e) => { const u = [...(form.nodeGroups || [])]; u[i] = { ...u[i], group_node_prefix_label: e.target.value }; setField('nodeGroups', u); }} />
                                                    <Autocomplete className={clsx(classes.input)} style={{ flex: 1 }}
                                                        options={computeNodeImages} getOptionLabel={(o) => o.name}
                                                        value={group.group_node_prefix ? { name: group.group_node_prefix } : null}
                                                        onChange={(e, v) => { const u = [...(form.nodeGroups || [])]; u[i] = { ...u[i], group_node_prefix: v?.name || '' }; setField('nodeGroups', u); }}
                                                        renderInput={(params) => <TextField {...params} label="Compute Node Image" variant="outlined" required />} />
                                                    <Autocomplete style={{ width: '25%' }}
                                                        options={getAvailableMachines()} getOptionLabel={(o) => o.name}
                                                        getOptionSelected={(o, v) => o.instance_id === v.instance_id}
                                                        value={nodeShapes.find(s => s.instance_id === group.instance_id) || null}
                                                        onChange={(e, v) => { const u = [...(form.nodeGroups || [])]; u[i] = { ...u[i], instance_id: v?.instance_id || '', selectedNodeShape: v }; setField('nodeGroups', u); }}
                                                        renderInput={(params) => <TextField {...params} label="Size *" variant="outlined" />} />
                                                    <TextField style={{ width: '100px' }} label="Count" type="number" variant="outlined" required
                                                        value={group.machines?.length || group.count || 0}
                                                        onChange={(e) => { const u = [...(form.nodeGroups || [])]; u[i] = { ...u[i], count: parseInt(e.target.value, 10) || 0 }; setField('nodeGroups', u); }}
                                                        inputProps={{ min: 0, max: 250 }} />
                                                    {(form.nodeGroups || []).length > 1 && (
                                                        <IconButton size="small" onClick={() => setField('nodeGroups', (form.nodeGroups || []).filter((_, idx) => idx !== i))}>
                                                            <Icon>delete</Icon>
                                                        </IconButton>
                                                    )}
                                                </div>
                                            </Box>
                                        ))}
                                    </SectionCard>
                                )}

                                {/* Commissioned Compute Nodes */}
                                {isCommissioned && (
                                    <SectionCard icon="memory" title="Commissioned Compute Nodes">
                                        {selectedDeviceFamily?.devices?.length > 1 ? (
                                            <div>
                                                <Typography variant="body2" style={{ marginBottom: '16px', color: '#666' }}>
                                                    Select additional commissioned machines to use as compute nodes.
                                                </Typography>
                                                {selectedDeviceFamily.devices
                                                    .filter(d => (d.id || d._id) !== (selectedPreconfiguredDevice?.id || selectedPreconfiguredDevice?._id))
                                                    .map(device => {
                                                        const deviceId = device.id || device._id;
                                                        const isSelected = selectedCommissionedComputeNodes.includes(deviceId);
                                                        return (
                                                            <Box key={deviceId} display="flex" alignItems="center" mb={1} p={1}
                                                                style={{ backgroundColor: isSelected ? '#e3f2fd' : '#f5f5f5', borderRadius: '4px', border: isSelected ? '1px solid #2196F3' : '1px solid #e0e0e0' }}>
                                                                <Checkbox checked={isSelected}
                                                                    onChange={(e) => setSelectedCommissionedComputeNodes(prev => e.target.checked ? [...prev, deviceId] : prev.filter(id => id !== deviceId))}
                                                                    color="primary" disabled={disableInputs} />
                                                                <Box ml={1}>
                                                                    <Typography variant="body1" style={{ fontWeight: 500 }}>{device.name}</Typography>
                                                                    <Typography variant="body2" style={{ color: '#666' }}>{device.ipAddress || device.public_ip} • {device.architecture || 'auto'}</Typography>
                                                                </Box>
                                                            </Box>
                                                        );
                                                    })}
                                                {selectedCommissionedComputeNodes.length > 0 && (
                                                    <Typography variant="body2" style={{ marginTop: '12px', color: '#4CAF50' }}>
                                                        {selectedCommissionedComputeNodes.length} compute node(s) selected
                                                    </Typography>
                                                )}
                                            </div>
                                        ) : (
                                            <Typography style={{ color: '#666' }}>
                                                {selectedDeviceFamily?.devices?.length === 1
                                                    ? 'No additional machines available.'
                                                    : 'Select a device family and head node first.'}
                                            </Typography>
                                        )}
                                    </SectionCard>
                                )}

                                <AdvancedSectionsBlock
                                    form={form} setForm={setForm} setField={setField}
                                    provider="maas" advanced={advanced} marketplace={marketplace} disableInputs={disableInputs}
                                />

                                {/* Advanced: Volume + Network + Packages */}
                                {advanced && (
                                    <SectionCard icon="settings" title="Advanced Settings">
                                        {!isCommissioned && (
                                            <div style={inputGroupStyle}>
                                                <TextField className={clsx(classes.input)} style={{ flex: 1 }}
                                                    label="Server Volume (GB)" variant="outlined"
                                                    value={form.serverVolumeSize || ''} onChange={(e) => setField('serverVolumeSize', e.target.value)} />
                                                <TextField className={clsx(classes.input)} style={{ flex: 1 }}
                                                    label="Node Volume (GB)" variant="outlined"
                                                    value={form.nodeVolumeSize || ''} onChange={(e) => setField('nodeVolumeSize', e.target.value)} />
                                            </div>
                                        )}
                                        <div style={inputGroupStyle}>
                                            <TextField className={clsx(classes.input)} style={{ flex: 1 }}
                                                label="Public IP" variant="outlined"
                                                value={form.publicIp || ''} onChange={(e) => setField('publicIp', e.target.value)} />
                                            <TextField select className={clsx(classes.input)} style={{ flex: 1 }}
                                                label="Share" variant="outlined"
                                                value={form.share || 'nfs'} onChange={(e) => setField('share', e.target.value)}>
                                                <MenuItem value="nfs">NFS</MenuItem>
                                                <MenuItem value="beegfs">BeeGFS</MenuItem>
                                            </TextField>
                                        </div>
                                        <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: '8px', marginTop: '16px' }}>Packages</Typography>
                                        <Paper style={{ display: 'flex', alignItems: 'center', padding: '2px 4px', marginBottom: '16px' }}>
                                            <InputBase inputRef={pkgRef} style={{ flex: 1, marginLeft: '8px' }} placeholder="Add a package" />
                                            <IconButton onClick={addPackage}><Icon>add</Icon></IconButton>
                                        </Paper>
                                        {(form.packages || []).map((pkg, i) => (
                                            <Box key={i} display="flex" alignItems="center" mb={0.5}>
                                                <Typography variant="body2" style={{ flex: 1 }}>{pkg}</Typography>
                                                <IconButton size="small" onClick={() => removePackage(pkg)}><Icon fontSize="small">delete</Icon></IconButton>
                                            </Box>
                                        ))}
                                    </SectionCard>
                                )}

                                <DescriptionSection form={form} setField={setField} />
                            </div>
                        </DisabledFormWrapper>
                    </DialogContent>
                </React.Fragment>
            )}
        </React.Fragment>
    );
};

export default MaasCluster;
